import { useState } from "react"
import type { FormEvent } from "react"
import { useNavigate } from "react-router-dom"
import { api } from "../api/client.ts"
import type { Case, ItemMaster, Org } from "../api/types.ts"
import { useSession } from "../auth/SessionProvider.tsx"
import { PhoneInput } from "../components/PhoneInput.tsx"
import { TrackingNoInput } from "../components/TrackingNoInput.tsx"
import { useApiQuery, useAsyncAction } from "../hooks/index.ts"
import { useI18n } from "../i18n/index.tsx"
import { toDateString } from "../lib/date-presets.ts"
import { CARRIERS, DEFAULT_CARRIER } from "../lib/carriers.ts"
import { intakeOrgChoice, orgLabel } from "../lib/intake-org.ts"
import { IntakeLines } from "../components/IntakeLines.tsx"
import { ReturnReasonOptions } from "../components/choice-options.tsx"
import { linesAreValid, newIntakeLine, toApiLines } from "../lib/intake-lines.ts"
import {
  Button,
  Card,
  CardHeader,
  ErrorNote,
  Field,
  FormGrid,
  Input,
  PageHeader,
  Select,
  Textarea,
} from "../ui/index.ts"

const today = () => toDateString()

export function CaseNewPage() {
  const { t, locale } = useI18n()
  const { user } = useSession()
  const navigate = useNavigate()
  const role = user?.role ?? "shipper"
  const create = useAsyncAction()

  // 관리자는 화주를 골라 보내야 한다. 화주 계정은 서버가 자기 화주로 정한다.
  const { needsOrg } = intakeOrgChoice(role, [])
  const orgsQuery = useApiQuery<{ orgs: Org[] }>(needsOrg ? "/api/orgs" : null)
  const orgs = orgsQuery.data?.orgs ?? []

  const [pickedOrgId, setPickedOrgId] = useState("")
  // 고를 수 있는 화주가 하나뿐이면 미리 골라 둔다. 사람이 고른 값이 언제나 이긴다.
  const { defaultOrgId } = intakeOrgChoice(role, orgs)
  const orgId = pickedOrgId || (defaultOrgId === null ? "" : String(defaultOrgId))

  const [form, setForm] = useState({
    receivedOn: today(),
    orderNo: "",
    carrier: String(DEFAULT_CARRIER),
    trackingNo: "",
    customerName: "",
    customerPhone: "",
    returnReason: "",
    note: "",
  })

  // 무엇이 몇 개 오는지. 한 줄은 늘 떠 있다 — 상품 없는 접수는 받지 않는다.
  const [lines, setLines] = useState(() => [newIntakeLine()])
  const mastersQuery = useApiQuery<{ items: ItemMaster[] }>("/api/item-masters")
  const masters = mastersQuery.data?.items ?? []

  const canSubmit = form.returnReason !== "" && linesAreValid(lines)

  const set = (key: keyof typeof form) => (value: string) =>
    setForm((current) => ({ ...current, [key]: value }))

  async function submit(event: FormEvent) {
    event.preventDefault()
    await create.run(async () => {
      const res = await api.post<{ case: Case }>("/api/cases", {
        ...(needsOrg ? { orgId: orgId ? Number(orgId) : null } : {}),
        receivedOn: form.receivedOn,
        orderNo: form.orderNo,
        carrier: form.carrier || null,
        trackingNo: form.trackingNo || null,
        customerName: form.customerName,
        customerPhone: form.customerPhone || null,
        returnReason: form.returnReason,
        note: form.note || null,
        lines: toApiLines(lines),
      })
      // 만든 건으로 바로 간다. 실패하면 여기 오지 않고 오류 문구가 뜬다.
      void navigate(`/cases/${res.case.id}`)
    })
  }

  return (
    <>
      <PageHeader title={t("caseNew.title")} />

      <form onSubmit={(event) => void submit(event)}>
        <ErrorNote error={create.error ?? orgsQuery.error ?? mastersQuery.error} />

        <Card>
          <CardHeader title={t("caseDetail.intake")} icon="inbox" />
          <FormGrid columns={2}>
            {needsOrg && (
              <Field label={t("case.org")} htmlFor="c-org" required>
                <Select
                  id="c-org"
                  required
                  value={orgId}
                  onChange={(event) => setPickedOrgId(event.target.value)}
                >
                  <option value="" disabled>
                    {t("caseNew.orgPlaceholder")}
                  </option>
                  {orgs.map((org) => (
                    <option key={org.id} value={org.id}>
                      {orgLabel(org, locale)}
                    </option>
                  ))}
                </Select>
              </Field>
            )}

            <Field label={t("case.receivedOn")} htmlFor="c-received" required>
              <Input
                id="c-received"
                type="date"
                required
                value={form.receivedOn}
                onChange={(event) => set("receivedOn")(event.target.value)}
              />
            </Field>

            <Field label={t("case.orderNo")} htmlFor="c-order" required>
              <Input
                id="c-order"
                required
                autoFocus
                value={form.orderNo}
                onChange={(event) => set("orderNo")(event.target.value)}
              />
            </Field>

            <Field label={t("case.customerName")} htmlFor="c-name" required rowStart>
              <Input
                id="c-name"
                required
                value={form.customerName}
                onChange={(event) => set("customerName")(event.target.value)}
              />
            </Field>

            <Field label={t("case.customerPhone")} htmlFor="c-phone">
              <PhoneInput
                id="c-phone"
                value={form.customerPhone}
                onChange={set("customerPhone")}
              />
            </Field>

            <Field label={t("case.returnReason")} htmlFor="c-reason" required rowStart>
              <Select
                id="c-reason"
                required
                value={form.returnReason}
                onChange={(event) => set("returnReason")(event.target.value)}
              >
                <ReturnReasonOptions />
              </Select>
            </Field>
          </FormGrid>
        </Card>

        <Card>
          <CardHeader title={t("lines.title")} icon="tag" />
          <p className="muted">{t("lines.hint")}</p>
          <IntakeLines lines={lines} masters={masters} onChange={setLines} />
        </Card>

        <Card>
          <CardHeader title={t("caseDetail.arrival")} icon="ship" />
          <FormGrid columns={2}>
            <Field label={t("case.carrier")} htmlFor="c-carrier">
              <Select
                id="c-carrier"
                value={form.carrier}
                onChange={(event) => set("carrier")(event.target.value)}
              >
                <option value="">{t("caseNew.carrierPlaceholder")}</option>
                {CARRIERS.map((carrier) => (
                  <option key={carrier} value={carrier}>
                    {t(`carrier.${carrier}`)}
                  </option>
                ))}
              </Select>
            </Field>

            <Field
              label={t("case.trackingNo")}
              htmlFor="c-tracking"
              hint={t("caseNew.trackingHint")}
            >
              <TrackingNoInput
                id="c-tracking"
                aria-describedby="c-tracking-msg"
                value={form.trackingNo}
                onChange={set("trackingNo")}
              />
            </Field>

            <Field label={t("case.note")} htmlFor="c-note" wide>
              <Textarea
                id="c-note"
                rows={3}
                value={form.note}
                onChange={(event) => set("note")(event.target.value)}
              />
            </Field>
          </FormGrid>
        </Card>

        <div className="inline">
          <Button type="submit" variant="primary" loading={create.busy} disabled={!canSubmit}>
            {t("caseNew.submit")}
          </Button>
          <Button onClick={() => void navigate("/cases")}>{t("common.cancel")}</Button>
        </div>
      </form>
    </>
  )
}
